import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, linkedSignal, output, signal } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, from, map, tap } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { AccountTeamFacade } from '@layout/account-team/facades/account-team.facade';
import { AccountTeamPanelMemberComponent } from '@layout/account-team/components/account-team-panel-member/account-team-panel-member.component';
import { OrganizationManagersStorageFacade } from '@core/organization-managers/facades/organization-managers-storage.facade';
import {
  DialogService,
  DialogSize,
  DialogReference,
  IconButtonComponent,
  SpinnerComponent,
  TooltipDirective,
} from '@kaseya-design-components';

@Component({
  selector: 'itc-account-team-panel',
  templateUrl: 'account-team-panel.component.html',
  styleUrl: 'account-team-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.mobile-view]': 'isMobileView()' },
  imports: [NgOptimizedImage, TranslatePipe, AccountTeamPanelMemberComponent, IconButtonComponent, SpinnerComponent, TooltipDirective],
})
export class AccountTeamPanelComponent {
  private readonly organizationManagersStorageFacade = inject(OrganizationManagersStorageFacade);
  private readonly accountTeamFacade = inject(AccountTeamFacade);
  private readonly dialogService = inject(DialogService);
  private readonly destroyRef = inject(DestroyRef);
  private dialogRef: DialogReference | null = null;

  readonly isMobileView = input(false);
  readonly isExpanded = input<boolean | undefined>(undefined, { alias: 'expanded' });
  readonly viewTeamClick = output<void>();
  readonly expanded = linkedSignal(() => {
    const inputValue = this.isExpanded();
    if (inputValue !== undefined) {
      return inputValue;
    }
    return this.organizationManagersStorageFacade.areOrganizationManagersCollapsed();
  });
  readonly members = this.accountTeamFacade.members;
  readonly loading = this.accountTeamFacade.loading;
  readonly error = this.accountTeamFacade.error;
  readonly isDialogLoading = signal(false);
  readonly hasPanelMembers = computed(() => {
    if (this.error()) {
      return false;
    }
    const members = this.members();
    return !!(members?.accountManager || members?.accountManagementManager);
  });

  constructor() {
    this.accountTeamFacade.loadAccountTeamMembers();
  }

  togglePanel(): void {
    this.expanded.update(expanded => !expanded);
    this.organizationManagersStorageFacade.setOrganizationManagersCollapsedState(this.expanded());
  }

  handlePanelKeydownSpace(event: Event): void {
    event.preventDefault();
    this.togglePanel();
  }

  openTeamDialog(): void {
    if (this.dialogRef) {
      return;
    }

    this.isDialogLoading.set(true);
    this.viewTeamClick.emit();
    this.loadDialog();
  }

  openBillingChatbot(): void {
    this.accountTeamFacade.openBillingChatbot();
  }

  private loadDialog(): void {
    from(import('@layout/account-team/components/account-team-dialog/account-team-dialog.component'))
      .pipe(
        map(({ AccountTeamDialogComponent }) =>
          this.dialogService.openDialog(AccountTeamDialogComponent, {
            width: DialogSize.Lg,
            styleClass: 'dialog dialog--narrow-mobile',
            dismissOnBackdropClick: true,
            focusOnShow: false,
          })
        ),
        tap(dialogRef => this.openDialog(dialogRef)),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isDialogLoading.set(false))
      )
      .subscribe();
  }

  private openDialog(dialogRef: DialogReference): void {
    this.dialogRef = dialogRef;
    this.dialogRef
      .onDismiss()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => (this.dialogRef = null));
  }
}
